import { useCallback } from 'react'
import { useDispatch, useSelector } from 'store'
import * as SearchLogAction from 'slicers/SearchSlicers/SearchLogSlice'
import * as TableAction from 'slicers/TableSlicers/TableSlice'
import * as TableLogAction from 'slicers/TableSlicers/TableLogSlice'
import * as PageAction from 'slicers/PageSlice'
import { inputF, promiseVoidF, voidF, voidNumberF } from 'types/FunctionTypes'
import { postAPI } from 'utils/apiUtil'
import { getNumberFromDateTimeString, getTimestampFromNumber } from 'utils/dateUtil'
import { convertNumber } from 'utils/numberUtil'
import { jigyosho, tokuisakiTenpo, user } from 'types/ReferTypes'
import { commonMsg } from 'assets/MessageCode'
import { requestLog, responseLog, getRequestLog } from 'utils/requestUtil'
import { F_NUM } from 'assets/FormatNumbers'

// 年月日 //================================//
export const useDateTime = (): [string, string, inputF, inputF] => {
  const dispatch = useDispatch()
  const ADD_YMD_FROM = useSelector((state) => state.SearchLogReducer.searchLog.ADD_YMD_FROM)
  const ADD_YMD_TO = useSelector((state) => state.SearchLogReducer.searchLog.ADD_YMD_TO)
  const changeAddYmdFrom = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const max = 99991231235959
      const min = 10000101000000
      let value = getNumberFromDateTimeString(e.target.value + 'T00:00:00')

      if (max < value) value = max
      if (min > value) value = F_NUM
      dispatch(SearchLogAction.changeAddYmdFrom(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(value, 14))))

      // FROMがTOより遅くなる場合、TOをFROMに合わせる
      // const valueTo = getNumberFromDateTimeString(e.target.value + 'T23:59:59')
      // if (ADD_YMD_TO !== F_NUM && value > ADD_YMD_TO) dispatch(SearchLogAction.changeAddYmdTo(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(valueTo, 14))))
    },
    [ADD_YMD_FROM, ADD_YMD_TO],
  )
  const changeAddYmdTo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      const max = 99991231235959
      const min = 10000101000000
      let value = getNumberFromDateTimeString(e.target.value + 'T23:59:59')

      if (max < value) value = max
      if (min > value) value = F_NUM
      dispatch(SearchLogAction.changeAddYmdTo(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(value, 14))))

      // TOがFROMより早くなる場合、FROMをTOに合わせる
      // const valueFrom = getNumberFromDateTimeString(e.target.value + 'T00:00:00')
      // if (value !== F_NUM && ADD_YMD_FROM > value) dispatch(SearchLogAction.changeAddYmdFrom(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(valueFrom, 14))))
    },
    [ADD_YMD_TO, ADD_YMD_FROM],
  )
  return [getTimestampFromNumber(ADD_YMD_FROM === -1 ? 0 : convertNumber(ADD_YMD_FROM, 8)), getTimestampFromNumber(convertNumber(ADD_YMD_TO, 8)), changeAddYmdFrom, changeAddYmdTo]
}

// 区分 //================================//
export const useKbn = (): [boolean, boolean, boolean, voidF, voidF, voidF] => {
  const dispatch = useDispatch()
  const torokuCheck = useSelector((state) => state.SearchLogReducer.searchLog.TOROKU_KBN.torokuCheck)
  const sakujoCheck = useSelector((state) => state.SearchLogReducer.searchLog.TOROKU_KBN.sakujoCheck)
  const koshinCheck = useSelector((state) => state.SearchLogReducer.searchLog.TOROKU_KBN.koshinCheck)
  const allCheckbox = [torokuCheck, sakujoCheck, koshinCheck]

  const isUncheckAll = (index: number, allCheckbox: boolean[]) => {
    const otherCheckbox = allCheckbox.filter((_, i) => {
      return i !== index
    })
    return (
      allCheckbox[index] &&
      otherCheckbox.every((r) => {
        return !r
      })
    )
  }

  const changeToroku = useCallback(() => {
    const index = 0
    dispatch(SearchLogAction.changeTourokuCheck())
    if (isUncheckAll(index, allCheckbox)) {
      // すべてのチェックボックスが外れる場合、削除、更新にチェックをつける
      dispatch(SearchLogAction.changeSakujoCheck())
      dispatch(SearchLogAction.changeKoshinCheck())
    }
  }, [...allCheckbox])
  const changeSakujo = useCallback(() => {
    const index = 1
    dispatch(SearchLogAction.changeSakujoCheck())
    if (isUncheckAll(index, allCheckbox)) {
      // すべてのチェックボックスが外れる場合、登録、更新にチェックをつける
      dispatch(SearchLogAction.changeTourokuCheck())
      dispatch(SearchLogAction.changeKoshinCheck())
    }
  }, [...allCheckbox])
  const changeKoshin = useCallback(() => {
    const index = 2
    dispatch(SearchLogAction.changeKoshinCheck())
    if (isUncheckAll(index, allCheckbox)) {
      // すべてのチェックボックスが外れる場合、登録、削除にチェックをつける
      dispatch(SearchLogAction.changeTourokuCheck())
      dispatch(SearchLogAction.changeSakujoCheck())
    }
  }, [...allCheckbox])
  return [torokuCheck, sakujoCheck, koshinCheck, changeToroku, changeSakujo, changeKoshin]
}

// 事業所コード //================================//
export const useJigyoshoCode = (): [number, inputF] => {
  const dispatch = useDispatch()
  const JIGYO_CD = useSelector((state) => state.SearchLogReducer.searchLog.JIGYO_CD)
  const loading = useSelector((state) => state.PageReducer.loading)
  const changeJigyoCode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!loading) dispatch(SearchLogAction.changeJigyoshoCode(e.target.value ? e.target.value : '-1'))
    },
    [JIGYO_CD, loading],
  )
  return [JIGYO_CD, changeJigyoCode]
}

// 事業所名 //================================//
export const useJigyoshoName = (): [string, promiseVoidF] => {
  const dispatch = useDispatch()
  const JIGYO_CD = useSelector((state) => state.SearchLogReducer.searchLog.JIGYO_CD)
  const JIGYO_NM = useSelector((state) => state.SearchLogReducer.searchLog.JIGYO_NM)
  const loading = useSelector((state) => state.PageReducer.loading)
  const submitJigyosho = useCallback(async () => {
    if (!loading) {
      if (JIGYO_CD <= 0) {
        dispatch(SearchLogAction.changeJigyoshoName(''))
        dispatch(SearchLogAction.changeJigyoshoError(false))
      } else if (JIGYO_CD > 0) {
        dispatch(PageAction.startLoading('...処理中'))
        await postAPI<{ JIGYO_CD: number }, jigyosho>(
          '/postJigyoName',
          { JIGYO_CD: JIGYO_CD },
          (r) => {
            dispatch(PageAction.deleteMessage())
            dispatch(SearchLogAction.changeJigyoshoName(r.body.JIGYO_NM))
            dispatch(SearchLogAction.changeJigyoshoError(r.alert))
          },
          () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(PageAction.endLoading()),
        )
      }
    }
  }, [JIGYO_CD, loading])
  return [JIGYO_NM, submitJigyosho]
}

// 得意先コード //================================//
export const useTokuisakiCode = (): [number, inputF] => {
  const dispatch = useDispatch()
  const TOKUI_CD = useSelector((state) => state.SearchLogReducer.searchLog.TOKUI_CD)
  const loading = useSelector((state) => state.PageReducer.loading)
  const changeTokuisakiCode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!loading) dispatch(SearchLogAction.changeTokuisakiCode(e.target.value ? e.target.value : '-1'))
    },
    [TOKUI_CD, loading],
  )
  return [TOKUI_CD, changeTokuisakiCode]
}

// 得意先名 //================================//
export const useTokuisakiName = (): [string, promiseVoidF] => {
  const dispatch = useDispatch()
  const TOKUI_CD = useSelector((state) => state.SearchLogReducer.searchLog.TOKUI_CD)
  const TOKUI_NM = useSelector((state) => state.SearchLogReducer.searchLog.TOKUI_NM)
  const TEN_CD = useSelector((state) => state.SearchLogReducer.searchLog.TEN_CD)
  const loading = useSelector((state) => state.PageReducer.loading)
  const submitTokuisaki = useCallback(async () => {
    if (!loading) {
      if (TOKUI_CD <= 0 || TEN_CD <= 0) {
        dispatch(SearchLogAction.changeTokuisakiName(''))
        dispatch(SearchLogAction.changeTenpoName(''))
        dispatch(SearchLogAction.changeTokuisakiError(false))
      } else if (TOKUI_CD > 0 && TEN_CD > 0) {
        dispatch(PageAction.startLoading('...処理中'))
        await postAPI<{ TOKUI_CD: number; TEN_CD: number }, tokuisakiTenpo>(
          '/postTokuiTenName',
          { TOKUI_CD: TOKUI_CD, TEN_CD: TEN_CD },
          (r) => {
            dispatch(PageAction.deleteMessage())
            dispatch(SearchLogAction.changeTokuisakiName(r.body.TOKUI_NM))
            dispatch(SearchLogAction.changeTenpoName(r.body.TEN_NM))
            dispatch(SearchLogAction.changeTokuisakiError(r.alert))
            dispatch(SearchLogAction.changeTenpoError(r.alert))
          },
          () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(PageAction.endLoading()),
        )
      }
    }
  }, [TOKUI_CD, TEN_CD, loading])
  return [TOKUI_NM, submitTokuisaki]
}

// 店舗コード //================================//
export const useTenpoCode = (): [number, inputF] => {
  const dispatch = useDispatch()
  const TEN_CD = useSelector((state) => state.SearchLogReducer.searchLog.TEN_CD)
  const loading = useSelector((state) => state.PageReducer.loading)
  const changeTenpoCode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!loading) dispatch(SearchLogAction.changeTenpoCode(e.target.value ? e.target.value : '-1'))
    },
    [TEN_CD, loading],
  )
  return [TEN_CD, changeTenpoCode]
}

// 店舗名 //================================//
export const useTenpoName = (): [string, promiseVoidF] => {
  const dispatch = useDispatch()
  const TEN_CD = useSelector((state) => state.SearchLogReducer.searchLog.TEN_CD)
  const TEN_NM = useSelector((state) => state.SearchLogReducer.searchLog.TEN_NM)
  const TOKUI_CD = useSelector((state) => state.SearchLogReducer.searchLog.TOKUI_CD)
  const loading = useSelector((state) => state.PageReducer.loading)
  const submitTenpo = useCallback(async () => {
    if (!loading) {
      if (TEN_CD <= 0 || TOKUI_CD <= 0) {
        dispatch(SearchLogAction.changeTenpoName(''))
        dispatch(SearchLogAction.changeTokuisakiName(''))
        dispatch(SearchLogAction.changeTenpoError(false))
      } else if (TEN_CD > 0 && TOKUI_CD > 0) {
        dispatch(PageAction.startLoading('...処理中'))
        await postAPI<{ TOKUI_CD: number; TEN_CD: number }, tokuisakiTenpo>(
          '/postTokuiTenName',
          { TOKUI_CD: TOKUI_CD, TEN_CD: TEN_CD },
          (r) => {
            dispatch(PageAction.deleteMessage())
            dispatch(SearchLogAction.changeTenpoName(r.body.TEN_NM))
            dispatch(SearchLogAction.changeTokuisakiName(r.body.TOKUI_NM))
            dispatch(SearchLogAction.changeTenpoError(r.alert))
            dispatch(SearchLogAction.changeTokuisakiError(r.alert))
          },
          () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(PageAction.endLoading()),
        )
      }
    }
  }, [TEN_CD, TOKUI_CD, loading])
  return [TEN_NM, submitTenpo]
}

// 登録者メールアドレス //================================//
export const useAddMail = (): [string, inputF] => {
  const dispatch = useDispatch()
  const ADD_MAIL = useSelector((state) => state.SearchLogReducer.searchLog.ADD_MAIL)
  const loading = useSelector((state) => state.PageReducer.loading)
  const changeAddMail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!loading) dispatch(SearchLogAction.changeAddMail(e.target.value))
    },
    [ADD_MAIL, loading],
  )
  return [ADD_MAIL, changeAddMail]
}

// 登録者名 //================================//
export const useAddName = (): [string, promiseVoidF] => {
  const dispatch = useDispatch()
  const ADD_MAIL = useSelector((state) => state.SearchLogReducer.searchLog.ADD_MAIL)
  const ADD_NAME = useSelector((state) => state.SearchLogReducer.searchLog.ADD_NAME)
  const loading = useSelector((state) => state.PageReducer.loading)
  const submitAddMail = useCallback(async () => {
    if (!loading) {
      if (ADD_MAIL.length <= 0) {
        dispatch(SearchLogAction.changeAddName(''))
        dispatch(SearchLogAction.changeAddNameError(false))
      } else if (ADD_MAIL.length > 0) {
        dispatch(PageAction.startLoading('...処理中'))
        await postAPI<{ MAIL: string }, user>(
          '/postUserName',
          { MAIL: ADD_MAIL },
          (r) => {
            dispatch(PageAction.deleteMessage())
            dispatch(SearchLogAction.changeAddName(r.body.NAME))
            dispatch(SearchLogAction.changeAddNameError(r.alert))
          },
          () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(PageAction.endLoading()),
        )
      }
    }
  }, [ADD_MAIL, loading])
  return [ADD_NAME, submitAddMail]
}

// メールアドレス //================================//
export const useMail = (): [string, inputF] => {
  const dispatch = useDispatch()
  const MAIL = useSelector((state) => state.SearchLogReducer.searchLog.MAIL)
  const loading = useSelector((state) => state.PageReducer.loading)
  const changeMail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!loading) dispatch(SearchLogAction.changeMail(e.target.value))
    },
    [MAIL, loading],
  )
  return [MAIL, changeMail]
}

// 名前 //================================//
export const useName = (): [string, promiseVoidF] => {
  const dispatch = useDispatch()
  const MAIL = useSelector((state) => state.SearchLogReducer.searchLog.MAIL)
  const NAME = useSelector((state) => state.SearchLogReducer.searchLog.NAME)
  const loading = useSelector((state) => state.PageReducer.loading)
  const submitMail = useCallback(async () => {
    if (!loading) {
      if (MAIL.length <= 0) {
        dispatch(SearchLogAction.changeName(''))
        dispatch(SearchLogAction.changeNameError(false))
      } else if (MAIL.length > 0) {
        dispatch(PageAction.startLoading('...処理中'))
        await postAPI<{ MAIL: string }, user>(
          '/postUserNameTokui',
          { MAIL: MAIL },
          (r) => {
            dispatch(PageAction.deleteMessage())
            dispatch(SearchLogAction.changeName(r.body.NAME))
            dispatch(SearchLogAction.changeNameError(r.alert))
          },
          () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(PageAction.endLoading()),
        )
      }
    }
  }, [MAIL, loading])
  return [NAME, submitMail]
}

// 初期描画フラグ //================================//
export const useInitView = (): [number, voidNumberF] => {
  const dispatch = useDispatch()
  const initView = useSelector((state) => state.SearchLogReducer.initView)

  const changeInitView = useCallback((initView: number) => {
    dispatch(SearchLogAction.changeInitView(initView))
  }, [])
  return [initView, changeInitView]
}

// ボタン項目押下処理 //================================//
export const useSearch = (): [promiseVoidF] => {
  const dispatch = useDispatch()
  const searchLog = useSelector((state) => state.SearchLogReducer.searchLog)
  const initView = useSelector((state) => state.SearchLogReducer.initView)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const page = useSelector((state) => state.TableReducer.page)
  const perPage = useSelector((state) => state.TableReducer.perPage)
  const loading = useSelector((state) => state.PageReducer.loading)

  // 検索ボタン押下処理 //================================//
  const clickSearch = useCallback(async () => {
    // if (!loading) return
    dispatch(PageAction.startLoading('...処理中'))
    dispatch(TableLogAction.changeSearchLogding(true))
    await postAPI<requestLog, responseLog>(
      '/postLogInfo',
      getRequestLog(isJapell, keyInfo, page, perPage, searchLog),
      (r) => {
        dispatch(PageAction.changeMessage(r))
        if (!r.error) {
          dispatch(TableLogAction.changeLogList(r.body.table))
          dispatch(TableLogAction.changeAllCount(r.body.allCount))
        }
      },
      () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)),
      () => {
        dispatch(PageAction.endLoading())
        dispatch(TableLogAction.changeSearchLogding(false))
        if (initView === 0) dispatch(SearchLogAction.changeInitView(1))
      },
    )
  }, [loading, searchLog, page, perPage, initView])

  return [clickSearch]
}

// 初期描画調整 //================================//
export const useInitProcess = () => {
  const dispatch = useDispatch()
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const initProcess = useCallback(() => {
    if (isJapell) dispatch(SearchLogAction.changeJigyoshoCode(String(keyInfo[0].JIGYO_CD)))
    dispatch(TableAction.changePerPage(100))
  }, [])
  return initProcess
}
